import * as React from "react";
import { Select } from "@radix-ui/themes";
import { SearchableSelect } from "./SearchableSelect";

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SelectRootProps {
  name: string;
  defaultValue?: string;
  value?: string;
  onValueChange: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  options: SelectOption[];
  className?: string;
  /**
   * Height utility for the trigger. Defaults to the 54px form control used
   * across the programme forms; pass e.g. "h-10" where it sits beside shorter
   * inputs. A height in `className` cannot do this job: Tailwind decides which
   * of two `h-*` utilities wins by stylesheet order, not by the order they
   * appear in the attribute, and `cn` is a plain join rather than
   * tailwind-merge — so `h-10` there loses to `h-[54px]` here.
   */
  triggerHeightClass?: string;
  /**
   * Classes for the dropdown panel — width and height, mostly.
   *
   * A select with a handful of options wants to size itself; one with a
   * hundred (the quarter-hour time list) needs to be told, or it runs the whole
   * height of the window and gets as wide as its widest measurement.
   */
  contentClassName?: string;
  /**
   * Where the dropdown is portalled. Defaults to <body>.
   *
   * PASS THE DIALOG'S CONTENT ELEMENT WHEN THIS SITS IN A RADIX MODAL.
   * A modal Dialog wraps itself in `react-remove-scroll`, which blocks wheel
   * events on anything outside its subtree. A dropdown portalled to <body> is
   * outside it, so the wheel did nothing and the list could only be moved by
   * dragging its scrollbar. Portalling into the dialog puts it inside the
   * allowed subtree and the wheel works again.
   */
  container?: HTMLElement | null;

  /**
   * Make the dropdown exactly as wide as the trigger, and open below it.
   *
   * Radix's default is `position="item-aligned"`: the panel sizes itself to
   * its widest item and floats over the trigger with the selected row on top
   * of it. For a short list that is fine. For the quarter-hour time list it
   * reads as a panel that has come loose — narrower than the field it belongs
   * to, covering the fields around it.
   *
   * `position="popper"` anchors it under the trigger and publishes
   * `--radix-select-trigger-width`, which is the only way to get the trigger's
   * real rendered width into CSS. `!` on both utilities because Radix Themes
   * sizes `.rt-SelectContent` itself, and a plain `w-*` would be decided by
   * stylesheet order rather than by this file.
   *
   * OPT-IN, not the default: 40-odd call sites share this component, and
   * flipping the positioning under all of them is not a change to make from
   * one screen's bug report.
   */
  matchTriggerWidth?: boolean;

  /**
   * Extra classes for every option row.
   *
   * Radix Themes sizes `.rt-SelectItem` (height, side padding, the checkmark
   * column) from `--select-item-*` variables that only exist under the
   * `.radix-themes` root. Content portalled outside it -- e.g. from a drawer --
   * loses them: rows get zero padding, the checkmark overlaps the label and
   * wrapped labels collide. Callers in that situation pass explicit spacing
   * here (with `!`, to outrank the Radix rule). Opt-in, like
   * `matchTriggerWidth`, so the other call sites are untouched.
   */
  itemClassName?: string;

  variant?: "default" | "borderless";

  /**
   * Adds a search box to the dropdown — for lists too long to scroll, like
   * Current Role Level (~90 options) and Primary Operator Function. Renders
   * SearchableSelect with the same value/onValueChange contract, so a call
   * site opts in with this one prop. Opt-in for the same reason as
   * `matchTriggerWidth`: 40-odd call sites share this component.
   */
  searchable?: boolean;
  /** Placeholder for the search box when `searchable`. */
  searchPlaceholder?: string;
}

export const SelectRoot = React.forwardRef<HTMLButtonElement, SelectRootProps>(
  (
    {
      name,
      defaultValue = undefined,
      value,
      onValueChange,
      onOpenChange,
      placeholder = "",
      options,
      className = "",
      triggerHeightClass = "h-[54px]",
      contentClassName = "",
      container,
      matchTriggerWidth = false,
      itemClassName = "",
      variant = "default",
      searchable = false,
      searchPlaceholder,
    },
    ref,
  ) => {
    if (searchable) {
      return (
        <SearchableSelect
          ref={ref}
          name={name}
          value={value ?? defaultValue}
          onValueChange={onValueChange}
          onOpenChange={onOpenChange}
          placeholder={placeholder}
          options={options}
          className={className}
          triggerHeightClass={triggerHeightClass}
          container={container}
          searchPlaceholder={searchPlaceholder}
        />
      );
    }

    const isBorderless = variant === "borderless";

    // Callers either drive this purely uncontrolled (defaultValue only, e.g.
    // TaskEditForm) or controlled (value + onValueChange, the react-hook-form
    // pattern used almost everywhere else). Controlled callers commonly pass
    // `value={watch(field) || undefined}`, which starts as `undefined` before
    // the field has a value and becomes a string once it does -- passing
    // `undefined` straight through makes Radix's Select.Root see "no value
    // prop" on mount (uncontrolled) and then a real value later (controlled),
    // triggering React's "changing from uncontrolled to controlled" warning.
    // Normalize to "" instead so a controlled caller's value is always a
    // string from the very first render.
    const isControlled = defaultValue === undefined;

    // Radix's Select.Trigger renders `<button data-placeholder>` wrapping an
    // inner `<span class="rt-SelectTriggerInner">`, and Radix Themes' own
    // stylesheet colors THAT inner span directly --
    //   .rt-SelectTrigger:where([data-placeholder]) :where(.rt-SelectTriggerInner) { color: var(--gray-a10); }
    // -- `--gray-a10` comes from Radix's own light/dark appearance system,
    // which isn't wired to this app's dark-mode toggle, so it resolves to a
    // near-black grey regardless of our theme. Setting `color` on the button
    // itself (the previous attempt here) never reaches it: that rule sets an
    // explicit color on the child span, which always wins over an inherited
    // value from the parent no matter what the parent's rule says. The fix
    // has to target that same descendant span, conditioned on the trigger's
    // own `[data-placeholder]` state, with `!important` to outrank Radix's
    // rule outright rather than out-specificity it.
    // `px-3` RATHER THAN RELYING ON RADIX'S OWN PADDING.
    //
    // Radix Themes pads the trigger with `padding-inline: var(--space-3)`, and
    // that token is declared on `.radix-themes` — the Theme root in main.tsx.
    // A Select inside a dialog is portalled to <body>, which is OUTSIDE that
    // root, so the variable resolves to nothing and the padding collapses to
    // zero: the value sat flush against the left border and the chevron
    // against the right. 12px is exactly what --space-3 evaluates to, so this
    // matches Radix everywhere else rather than overriding it.
    const baseTrigger =
      "w-full min-w-0 px-3 text-theme-text bg-[var(--theme-input-bg)] border border-theme-border rounded-[14px] " +
      "[&[data-placeholder]_.rt-SelectTriggerInner]:!text-[var(--theme-input-placeholder)]";

    const triggerClassName = isBorderless
      ? `w-full min-w-0 text-theme-text bg-transparent border-none
         [&[data-placeholder]_.rt-SelectTriggerInner]:!text-theme-text-muted ${className}`
      : `${baseTrigger} ${triggerHeightClass} ${className}`;

    return (
      <Select.Root
        name={name}
        onValueChange={onValueChange}
        onOpenChange={onOpenChange}
        {...(isControlled ? { value: value ?? "" } : { defaultValue })}
      >
        <Select.Trigger
          ref={ref}
          placeholder={placeholder}
          className={triggerClassName}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "16px",
          }}
        />

        <Select.Content
          container={container ?? undefined}
          // See `matchTriggerWidth`: popper is what publishes the trigger width.
          {...(matchTriggerWidth ? { position: "popper" as const } : {})}
          /**
           * z-[70] IS LOAD-BEARING, NOT DECORATION.
           *
           * Radix Themes ships NO z-index on select content, so it computes to
           * `auto` — and the popper wrapper copies that computed value onto
           * itself (`zIndex: contentZIndex` in react-popper). A positioned
           * element with `z-index: auto` always paints BELOW one with a real
           * z-index, whatever the DOM order, so inside any dialog at z-50 the
           * dropdown opened correctly and rendered underneath the overlay —
           * which reads as "the dropdown will not open".
           *
           * 70 clears the dialog (50) and the editor popovers (60).
           *
           * `max-h-[320px]` is the other half: Radix Themes puts no height cap
           * on the panel, so a long list runs the full height of the window.
           * Radix wraps the viewport in a ScrollArea, so capping the panel
           * makes it scroll rather than clip. Override per call site with
           * `contentClassName`.
           */
          className={`
            z-[70]
            max-h-[320px]
            bg-theme-bg-surface
            border border-theme-border
            text-theme-text
            shadow-md rounded-md
            ${
              matchTriggerWidth
                ? "!w-[var(--radix-select-trigger-width)] !min-w-[var(--radix-select-trigger-width)]"
                : ""
            }
            ${contentClassName}
          `}
        >
          {options?.map((option) => (
            <Select.Item
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={`
                text-theme-text
                data-[highlighted]:bg-theme-accent-subtle
                data-[highlighted]:text-theme-accent
                data-[state=checked]:bg-theme-accent-subtle
                data-[state=checked]:text-theme-accent
                cursor-pointer
                ${itemClassName}
              `}
            >
              {option.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    );
  },
);

SelectRoot.displayName = "SelectRoot";
